import type { FunctionComponent, MouseEvent, MouseEventHandler, ReactNode } from 'react';
import { useCallback } from 'react';
import Helmet from 'react-helmet';
import * as _ from 'lodash';
import classNames from 'classnames';
import type { InjectedRouter } from 'react-router';
import { Link } from 'react-router';
import { ARCHIVE_AND_LICENSE_UPLOAD, DISABLE_GITHUB_APP } from '../../../../shared/featureFlags/flagNames';
import { window } from '../../../globals';
import { isFeatureEnabled } from '../../../helpers/features';
import { useDispatchOnMount } from '../../../hooks';
import NavigationStore from '../../../redux/navigation';
import type { NavigationState } from '../../../redux/navigation/types';
import type { RouterProps } from '../../../types';
import { capitalizeSnakeCaseWord, verify_subscription } from '../../../utils';
import BulkImportPageCard from './BulkImportPageCard';
import type { IconClassName } from '../../generic/Icon';
import Icon, { icons, IconText } from '../../generic/Icon';
import { DocsLink } from '../../generic/Link';
import Subnav from '../../generic/Subnav';
import Text from '../../generic/Text';
import CLIUploadGuide from '../upload/CLIUploadGuide';
import CreateProjectPage from '../CreateProjectPage';
import { AzureLogo } from './icons';
import ServiceImport from './ServiceImport';
import cardStyles from './BulkImportPageCard.module.less';
import { onPremDeploymentFeature } from '../../settings/billing/features/consts';
import { Badge, BadgeColors } from '../../generic';

type BulkImportPageProps = RouterProps & {
  router: InjectedRouter;
};

const AutoBuildHeader = 'Quick Import';
const AutoBuildDesc =
  'Automatically analyze from code host for easy initial results.';
const ProvidedBuildHeader = 'Integrate Locally (CLI)';
const ProvidedBuildDesc =
  'Use your personal or build machine for accurate, secure & performant results.';
const SBOMBuildHeader = 'Import SBOM';
const SBOMBuildDesc = 'Analyze and search your SBOM (CycloneDX and SPDX).';

const BulkImportPage: FunctionComponent<BulkImportPageProps> = (props) => {
  useDispatchOnMount<[Partial<NavigationState>]>(
    NavigationStore.actions.setNavState,
    {
      expand: 'expanded-sm',
      subnav: null,
      navProps: {},
    },
  );

  const onClickOnpremMethod = useCallback((e: MouseEvent) => {
    if (!window.on_prem) {
      window.showPlanSelectorModal({
        tier: 'Enterprise',
        focusFeature: onPremDeploymentFeature,
      });
      e.preventDefault();
    }
  }, []);

  const onClickPremiumUploadMethod = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    window.showPlanSelectorModal();
  }, []);

  const renderLastImportOptions = () => {
    const isPremium = verify_subscription('premium');
    const shouldShowOtherOrPublic = window.on_prem || window.user?.super;

    return (
      <>
        <div className="import-link">
          <ImportLink
            title="Azure Repos"
            color="#0078D4"
            absoluteHref="/projects/import/azure_repos"
            svgIcon={<AzureLogo />}
          />
        </div>
        {isFeatureEnabled(ARCHIVE_AND_LICENSE_UPLOAD) &&
          <div className="import-link">
            {isPremium ? (
              <ImportLink
                title="Upload Archive"
                color="#999"
                absoluteHref="/projects/import/upload"
                icon={icons.upload}
              />
            ) : (
              <ImportLink
                title="Upload Archive"
                color="#999"
                absoluteHref="/projects/import/cloud"
                onClick={onClickPremiumUploadMethod}
                icon={icons.upload}
              />
            )}
          </div>}
        {shouldShowOtherOrPublic && (
          <div className="import-link">
            <ImportLink
              title="Other / Public"
              color="#999"
              href="manual"
              icon={icons.globe}
            />
          </div>
        )}
      </>
    );
  };

  const { service } = props.params;

  // Choose import option
  if (service == null) {
    // Only allow premium organizations to access this, but allow the temporary free tier to access it as well.

    return (
      <div className="bg-bubblestream">
        <Helmet title="Add Projects" titleTemplate="%s | FOSSA" />
        <div className="container margin-top text-center">
          <Text.H1>
            <b>Add Projects</b>
          </Text.H1>
          <div className={classNames(cardStyles.cards, 'animated', 'fadeInDown', 'fast')}>
            <BulkImportPageCard
              title={ProvidedBuildHeader}
              isRecommended
              linkTo="/projects/import/cli"
              featureIcons={[icons.upload]}
              description={ProvidedBuildDesc}
              buttonText="View Guide"
              subtitle="No Auth / Code Access"
              subtitleIcon={icons.eyeSlash}
            />
            <BulkImportPageCard
              title={AutoBuildHeader}
              linkTo="/projects/import/cloud"
              featureIcons={[icons.github, icons.bitbucket, icons.gitlab]}
              description={AutoBuildDesc}
              buttonText="Continue"
              subtitle="Authentication Required"
              subtitleIcon={icons.lock}
            />
            <BulkImportPageCard
              title={SBOMBuildHeader}
              linkTo="/projects/import/upload/sbom"
              featureIcons={[icons.cyclone, icons.spdx]}
              description={SBOMBuildDesc}
              buttonText="Upload"
            />
          </div>
          <p className="padded padded-small text-center">
            Troubleshoot by viewing{' '}
            <DocsLink to="importing-a-project">
              <u>our docs</u>
            </DocsLink>
            .
          </p>
        </div>
      </div>
    );
  }

  // Manual Import
  if (service === 'manual' && (window.on_prem || window.user?.super)) {
    return (
      <div>
        <Helmet title="Manual Import" titleTemplate="%s | FOSSA" />
        <CreateProjectPage {...props} />
      </div>
    );
  }

  // Cloud Import (index)
  if (service === 'cloud') {
    return (
      <div className="container margin-top">
        <Helmet title="Choose Code Host" titleTemplate="%s | FOSSA" />
        <div className="col-md-6 col-md-offset-3">
          <div className="text-center">
            <h2>
              <b>{AutoBuildHeader}</b>
            </h2>
            <p>{AutoBuildDesc}</p>
            <Link className="btn btn-link" to="/projects/import">
              <IconText icon={icons.arrowLeft} text="Go Back" />
            </Link>
          </div>
          <br />
          <div className="animated fadeInUp fast">
            <div className="import-links-row">
              {!isFeatureEnabled(DISABLE_GITHUB_APP) && (
                <div className="import-link">
                  <ImportLink
                    title="GitHub App"
                    href="github-app"
                    color="#3a3a3a"
                    icon={icons.github}
                  />
                </div>
              )}
              <div className="import-link">
                <ImportLink
                  title="GitHub OAuth"
                  href="github"
                  color="#3a3a3a"
                  icon={icons.github}
                />
              </div>
              <div className="import-link">
                <ImportLink
                  title="Bitbucket.org"
                  href="bitbucket_cloud"
                  color="#305178"
                  icon={icons.bitbucket}
                />
              </div>
              <div className="import-link">
                <ImportLink
                  title="GitLab"
                  color="#d4361a"
                  href="gitlab"
                  icon={icons.gitlab}
                />
              </div>
              <div className="import-link">
                <ImportLink
                  title="Bitbucket Server"
                  color="#305178"
                  href="bitbucket_server"
                  icon={icons.bitbucket}
                  onClick={onClickOnpremMethod}
                />
              </div>
              {renderLastImportOptions()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // CLI Upload Instructions
  if (service === 'cli') {
    return (
      <div className="container margin-top">
        <Helmet title="Install FOSSA CLI" titleTemplate="%s | FOSSA" />
        <div className="col-md-6 col-md-offset-3">
          <div className="text-center">
            <Text.H1>
              <b>{ProvidedBuildHeader}</b>
            </Text.H1>
            <p
              className="text-center"
              style={{ maxWidth: '24rem', margin: '0 auto 16px' }}
            >
              {ProvidedBuildDesc}
            </p>
            <Link className="btn btn-link" to="/projects/import">
              <IconText icon={icons.arrowLeft} text="Go Back" />
            </Link>
          </div>
          <br />
          <div className="animated fadeInUp fast">
            <CLIUploadGuide />
          </div>
        </div>
      </div>
    );
  }

  // Proper service import
  // TODO: validate import method
  return (
    <div>
      <Helmet title={`Import From ${capitalizeSnakeCaseWord(service)}`} titleTemplate="%s | FOSSA" />
      <Subnav>
        <Subnav.Item to="/projects/import/cloud">
          <IconText icon={icons.arrowLeft} text="Back to Services" />
        </Subnav.Item>
      </Subnav>
      <div className="container margin-top">
        <ServiceImport location={props.location} service={service} />
      </div>
    </div>
  );
};

const ImportLink: FunctionComponent<{
  color: string;
  icon?: IconClassName;
  title: string;
  href?: string;
  absoluteHref?: string;
  onClick?: MouseEventHandler;
  svgIcon?: ReactNode;
}> = ({
  color,
  icon,
  title,
  href,
  absoluteHref,
  onClick = _.noop,
  svgIcon,
}) => {
  return (
    <Link
      onClick={onClick}
      to={absoluteHref || `/projects/import/${href || title.toLowerCase()}`}
      activeClassName="active"
      className="card text-center"
      style={{
        position: 'relative',
        borderTop: `solid 3px ${color}`,
        color,
      }}
    >
      {/* TODO: Remove once the GitHub App is fully GA. */}
      {href === 'github-app' && (
        <div className={cardStyles.previewBadge}>
          <Badge color={BadgeColors.DarkGray}>Preview</Badge>
        </div>
      )}
      {icon ? (
        <span className="import-link-icon margin-bottom-xs">
          <Icon icon={icon} />
        </span>
      ) : (
        svgIcon
      )}
      <h4 style={{ color }}>{title}</h4>
    </Link>
  );
};

export default BulkImportPage;
